import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, withCtx as _withCtx, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "page-max-width q-mx-auto q-pa-sm" }
const _hoisted_2 = { key: 0 }

import { ref, toRefs, computed, onMounted, onBeforeUnmount } from 'vue';
import { useQuasar } from 'quasar';

// Personal components
import QCustomFab from '@components/QCustomFab.vue';
import QPageTitle from '@components/QPageTitle.vue';

// Personal libs
import {
  convertBackendDateToDate,
  convertDateToSimpleDate,
} from '@shared/lib/date.lib';
import { scrollToTop } from '@shared/lib/tools.lib';
import bus from '@shared/lib/eventbus.lib';
import { BusEventEnum } from '@shared/enum/bus/bus-events.enum';
import { $t } from '@i18n/utils';
import { RouteEnum } from 'src/shared/enum/route.enum';
import { dbGetChangelog } from '@data/datasource/local/changelog.database';

/**
 * Globals
 */
const a11yAuditVersion = '02.00.00';

/**
 * Data
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'A11yPage',
  props: {
  displayTitle: { required: false, type: Boolean, default: true },
},
  setup(__props) {

// Vendor
const $q = useQuasar();

/**
 * Static data
 */
const showGoToTopButton = ref<boolean | undefined>(false);
const a11yAuditDate = ref<string>();

/**
 * Computed properties
 */
const props = __props;
const { displayTitle } = toRefs(props);
const appUrl = computed<string>(() => process.env.FRONTEND.host);

/**
 * Methods
 */
function onScroll(position) {
  showGoToTopButton.value = position.position != 0;
}

async function getA11yAuditDate() {
  const changelog = await dbGetChangelog();
  const auditRelease = changelog?.releases?.find((r) => r.version === a11yAuditVersion);

  if (auditRelease?.deployedAt) {
    a11yAuditDate.value = convertDateToSimpleDate(
      convertBackendDateToDate(auditRelease?.deployedAt),
    );
  } else {
    a11yAuditDate.value = 'inconnue';
  }
}

/**
 * Hooks
 */
onMounted(() => {
  // Events
  bus.on(BusEventEnum.SCROLL_EVENT, (position) => {
    if (displayTitle.value) {
      onScroll(position);
    }
  });

  getA11yAuditDate();
});

onBeforeUnmount(() => {
  showGoToTopButton.value = undefined;

  bus.off(BusEventEnum.SCROLL_EVENT);
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(`cgu full-width q-mx-auto page-container ${
      _unref($q).dark.isActive ? 'text-white' : 'text-black'
    }`)
  }, [
    _createElementVNode("div", _hoisted_1, [
      (_unref(displayTitle))
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createVNode(QPageTitle, { title: "Accessibilité" })
          ]))
        : _createCommentVNode("v-if", true),
      _createCommentVNode(" H3 - Qu'est-ce que l'accessibilité numérique ? "),
      _cache[6] || (_cache[6] = _createElementVNode("h3", null, "Qu'est-ce que l'accessibilité numérique ?", -1 /* HOISTED */)),
      _cache[7] || (_cache[7] = _createElementVNode("p", null, " Un site web accessible est un site qui permet à tous les internautes d'accéder à ses contenus sans difficulté, y compris aux personnes qui présentent un handicap et utilisent des logiciels ou matériels spécialisés. ", -1 /* HOISTED */)),
      _cache[8] || (_cache[8] = _createElementVNode("p", null, "Un site accessible permet par exemple de :", -1 /* HOISTED */)),
      _cache[9] || (_cache[9] = _createElementVNode("ul", null, [
        _createElementVNode("li", null, " Naviguer avec des synthèses vocales ou des plages braille (notamment utilisées par les internautes aveugles ou malvoyants). "),
        _createElementVNode("li", null, " Personnaliser l'affichage du site selon ses besoins (grossissement des caractères, modification des couleurs, etc.). "),
        _createElementVNode("li", null, " Naviguer sans utiliser la souris, avec le clavier uniquement ou via un écran tactile. ")
      ], -1 /* HOISTED */)),
      _createCommentVNode(" H3 - Déclaration d'accessibilité "),
      _cache[10] || (_cache[10] = _createElementVNode("h3", null, "Déclaration d'accessibilité", -1 /* HOISTED */)),
      _cache[11] || (_cache[11] = _createElementVNode("p", null, " ESP Kobudo s'engage à rendre son site internet accessible conformément à l'article 47 de la loi n° 2005-102 du 11 février 2005. ", -1 /* HOISTED */)),
      _createElementVNode("p", null, " Cette déclaration d'accessibilité s'applique au site " + _toDisplayString(appUrl.value) + ". ", 1 /* TEXT */),
      _cache[12] || (_cache[12] = _createStaticVNode("<h4>Etat de conformité</h4><p> Le site est partiellement conforme au référentiel général d&#39;amélioration de l&#39;accessibilité (RGAA), version 4.1. </p><h5>Résultats des tests</h5><p> L&#39;audit de conformité, réalisé par Brice Lallement, sur un échantillon de 10 pages, révèle que 92,31% des critères RGAA v4.1 sont respectés. </p><h4>Contenus non accessibles</h4><p>Les contenus listés ci-dessous ne sont pas accessibles pour les raisons suivantes.</p><h5>Non conformité</h5><ul><li> Formulaires : les labels des champs de saisie apparaissent après les champs de saisie eux-même (dans le code HTML). Ce point fait l&#39;objet d&#39;une dérogation car il est tributaire d&#39;une librairie tierce (Quasar). </li><li> Formulaires : les messages d&#39;erreurs qui apparaissent à proximité des champs ne sont pas reliés par la relation aria-describedby=&quot;id_erreur&quot;. Ce point fait l&#39;objet d&#39;une dérogation car il est tributaire d&#39;une librairie tierce (Quasar). </li></ul><h5>Dérogations pour charge disproportionnée</h5><ul><li> Les vidéos n&#39;ont pas de transcription textuelle. La charge nécessaire est trop élevée. </li></ul><h4>Établissement de cette déclaration d&#39;accessibilité</h4>", 11)),
      _createElementVNode("p", null, [
        _createTextVNode(" Cette déclaration a été établie le " + _toDisplayString(a11yAuditDate.value) + ". ", 1 /* TEXT */),
        _cache[0] || (_cache[0] = _createElementVNode("span", { style: {"display":"none"} }, "Elle a été mise à jour le [JJ mois AAAA].", -1 /* HOISTED */))
      ]),
      _cache[13] || (_cache[13] = _createStaticVNode("<h5>Technologies utilisées pour la réalisation du site</h5><ul><li>HTML 5</li><li>CSS</li><li>Javascript</li></ul><h6>Environnement de test</h6><p>Agents utilisateurs, technologies d&#39;assistance et outils utilisés pour vérifier l&#39;accessibilité</p><p> Les vérifications de restitution de contenus ont été réalisées avec les configurations suivantes : </p><ul><li>Firefox et NVDA</li></ul><h6>Les outils utilisés lors de l&#39;évaluation</h6><p> Les outils de vérification du code suivants ont été utilisés afin de vérifier la conformité aux tests du RGAA : </p><ul><li>Colour Contrast Analyser</li><li>Extension Web Developer</li><li>Extension Stylus</li><li>Extension headingsMap</li><li>Extension WCAG color contrast checker</li><li>Extension Assistant RGAA</li><li>Outils pour développeurs intégrés au navigateur Firefox</li></ul><h6>Pages du site ayant fait l&#39;objet de la vérification de conformité</h6><ol><li>Accueil</li><li>Programmes</li><li>Contact</li><li>Connexion</li><li>Mot de passe oublié</li><li>CGU</li><li>Admin - utilisateurs (accès privé)</li><li>Admin - vidéos (accès privé)</li><li>Mon compte (accès privé)</li><li>Notes de version (accès privé)</li></ol><h4>Retour d&#39;information et contact</h4><p> Il est important de rappeler qu&#39;en vertu de l&#39;article 11 de la loi de février 2005 : la personne handicapée a droit à la compensation des conséquences de son handicap, quels que soient l&#39;origine et la nature de sa déficience, son âge ou son mode de vie. </p><p> ESP Kobudo s&#39;engage à prendre les moyens nécessaires afin de donner accès, dans un délai raisonnable, aux informations et fonctionnalités recherchées par la personne handicapée, que le contenu fasse l&#39;objet d&#39;une dérogation ou non. </p><p> ESP Kobudo invite les personnes qui rencontreraient des difficultés à la contacter afin qu&#39;une assistance puisse être apportée (alternative accessible, information et contenu donnés sous une autre forme) : </p>", 15)),
      _createElementVNode("ul", null, [
        _createElementVNode("li", null, [
          _createVNode(_component_router_link, {
            to: `/${_unref(RouteEnum).CONTACT}`,
            class: _normalizeClass(
              _unref($q).dark.isActive ? 'router-link-white' : 'router-link-black'
            )
          }, {
            default: _withCtx(() => _cache[1] || (_cache[1] = [
              _createTextVNode(" envoyez un message via notre formulaire de contact ")
            ])),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["to", "class"])
        ])
      ]),
      _cache[14] || (_cache[14] = _createElementVNode("h4", null, "Voies de recours", -1 /* HOISTED */)),
      _cache[15] || (_cache[15] = _createElementVNode("p", null, "Cette procédure est à utiliser dans le cas suivant.", -1 /* HOISTED */)),
      _cache[16] || (_cache[16] = _createElementVNode("p", null, " Si vous constatez un défaut d'accessibilité vous empêchant d'accéder à un contenu ou une fonctionnalité du site, que vous nous le signalez et que vous ne parvenez pas à obtenir une réponse de notre part, vous êtes en droit de faire parvenir vos doléances ou une demande de saisine au Défenseur des droits. ", -1 /* HOISTED */)),
      _cache[17] || (_cache[17] = _createElementVNode("p", null, " Vous avez signalé au responsable du site Internet un défaut d'accessibilité qui vous empêche d'accéder à un contenu ou à un des services du portail et vous n'avez pas obtenu de réponse satisfaisante. ", -1 /* HOISTED */)),
      _cache[18] || (_cache[18] = _createElementVNode("p", null, "Plusieurs moyens sont à votre disposition :", -1 /* HOISTED */)),
      _createElementVNode("ul", null, [
        _createElementVNode("li", null, [
          _createElementVNode("a", {
            href: "https://formulaire.defenseurdesdroits.fr/",
            target: "_blank",
            class: _normalizeClass(
              _unref($q).dark.isActive ? 'router-link-white' : 'router-link-black'
            ),
            rel: "noopener noreferrer"
          }, "écrire un message au Défenseur des droits", 2 /* CLASS */),
          _cache[2] || (_cache[2] = _createTextVNode(" ; "))
        ]),
        _createElementVNode("li", null, [
          _createElementVNode("a", {
            href: "https://www.defenseurdesdroits.fr/saisir/delegues",
            target: "_blank",
            class: _normalizeClass(
              _unref($q).dark.isActive ? 'router-link-white' : 'router-link-black'
            ),
            rel: "noopener noreferrer"
          }, "contacter le délégué du Défenseur des droits dans votre région", 2 /* CLASS */),
          _cache[3] || (_cache[3] = _createTextVNode(" ; "))
        ]),
        _cache[4] || (_cache[4] = _createElementVNode("li", null, "téléphoner au 09 69 39 00 00 ;", -1 /* HOISTED */)),
        _cache[5] || (_cache[5] = _createElementVNode("li", null, [
          _createTextVNode(" envoyer un courrier par la poste (gratuit, ne pas mettre de timbre) :"),
          _createElementVNode("br"),
          _createTextVNode(" Défenseur des droits"),
          _createElementVNode("br"),
          _createTextVNode(" Libre réponse 71120"),
          _createElementVNode("br"),
          _createTextVNode(" 75342 Paris CEDEX 07 ")
        ], -1 /* HOISTED */))
      ]),
      _createCommentVNode(" <q-space class=\"q-mb-xl\" /> "),
      _createCommentVNode(" Got to top button "),
      _createVNode(QCustomFab, {
        display: _unref(displayTitle) && showGoToTopButton.value === true,
        label: _unref($t)('generic.goToTop.page'),
        alt: _unref($t)('generic.goToTop.page'),
        action: _unref(scrollToTop)
      }, null, 8 /* PROPS */, ["display", "label", "alt", "action"])
    ])
  ], 2 /* CLASS */))
}
}

})